import * as React from 'react';

export type TooltipTriggerMode = 'hover' | 'click' | 'focus';

export type ExclusiveTriggerHandlerProps = {
  onPointerEnter?: React.PointerEventHandler;
  onPointerLeave?: React.PointerEventHandler;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onClick?: React.MouseEventHandler;
};

const NATIVELY_FOCUSABLE_TAGS: ReadonlySet<string> = new Set([
  'a',
  'button',
  'input',
  'select',
  'textarea',
]);

export function composeEventHandlers<E extends React.SyntheticEvent>(
  existing: ((event: E) => void) | undefined,
  next: ((event: E) => void) | undefined,
): ((event: E) => void) | undefined {
  if (!existing) return next;
  if (!next) return existing;
  return (event) => {
    existing(event);
    next(event);
  };
}

export function buildExclusiveTriggerHandlers(
  mode: Exclude<TooltipTriggerMode, 'hover'>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
): ExclusiveTriggerHandlerProps {
  if (mode === 'focus') {
    return {
      onFocus: () => setOpen(true),
      onBlur: () => setOpen(false),
    };
  }
  return {
    onClick: () => {
      setOpen((prev) => !prev);
    },
  };
}

/** Props merged onto the trigger child via Radix `Slot` (exclusive click/focus modes). */
export function getExclusiveTriggerSlotProps(
  child: React.ReactElement,
  handlers: ExclusiveTriggerHandlerProps,
  options?: { ariaDescribedBy?: string },
): Record<string, unknown> {
  const props = child.props as Record<string, unknown>;
  return {
    onPointerEnter: composeEventHandlers(
      props.onPointerEnter as React.PointerEventHandler | undefined,
      handlers.onPointerEnter,
    ),
    onPointerLeave: composeEventHandlers(
      props.onPointerLeave as React.PointerEventHandler | undefined,
      handlers.onPointerLeave,
    ),
    onFocus: composeEventHandlers(
      props.onFocus as React.FocusEventHandler | undefined,
      handlers.onFocus,
    ),
    onBlur: composeEventHandlers(
      props.onBlur as React.FocusEventHandler | undefined,
      handlers.onBlur,
    ),
    onClick: composeEventHandlers(
      props.onClick as React.MouseEventHandler | undefined,
      handlers.onClick,
    ),
    ...(options?.ariaDescribedBy
      ? { 'aria-describedby': options.ariaDescribedBy }
      : {}),
  };
}

/**
 * Pull the single React element out of `children`, returning `null` if
 * the trigger is not a single inspectable element.
 */
export function getOnlyChildElement(
  children: React.ReactNode,
): React.ReactElement | null {
  const arr = React.Children.toArray(children).filter(
    (c) => c !== null && c !== undefined && c !== '',
  );
  if (arr.length !== 1) return null;
  const only = arr[0];
  return React.isValidElement(only) ? only : null;
}

/**
 * Native focusable HTML tags (`button`, `a`, …): Radix `asChild` merges trigger
 * props (including `aria-describedby`) onto the focusable element for SR users.
 * Composite children and Puck slot outputs use the wrapper-span path instead.
 */
export function isChildNativeFocusable(children: React.ReactNode): boolean {
  const only = getOnlyChildElement(children);
  if (!only) return false;
  if (typeof only.type !== 'string') return false;
  if (!NATIVELY_FOCUSABLE_TAGS.has(only.type)) return false;
  const props = only.props as { disabled?: boolean } | undefined;
  return !props?.disabled;
}

/**
 * Wrapper-span path only: skip `tabIndex={0}` when the child already has a tab stop
 * (explicit `tabIndex` or hen-ui `Button` / `Link`).
 */
export function isChildAlreadyKeyboardFocusable(
  children: React.ReactNode,
): boolean {
  const only = getOnlyChildElement(children);
  if (!only) return false;
  const props = only.props as
    | { tabIndex?: number; disabled?: boolean }
    | undefined;
  if (typeof props?.tabIndex === 'number') return true;
  if (typeof only.type === 'string') return false;
  const displayName =
    (only.type as { displayName?: string }).displayName ??
    (only.type as { name?: string }).name ??
    '';
  return displayName === 'Button' || displayName === 'Link';
}
